import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { Check, ChevronDown, Landmark, Loader2 } from "lucide-react";
import { useState } from "react";

import type { Bank } from "@/types/banks";

import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList
} from "@/components/ui/command";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface BankSearchProps<T extends FieldValues> {
	name: FieldPath<T>;
	control: Control<T>;
	banks: Bank[];
	isLoading: boolean;
}

export function BankSearch<T extends FieldValues>({ banks, control, name, isLoading }: BankSearchProps<T>) {
	const [value, setValue] = useState("");
	const [open, setOpen] = useState(false);

	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<Label htmlFor="bankName" className="text-right">
						Bank name
					</Label>
					<FormControl>
						<Popover open={open} onOpenChange={setOpen} modal>
							<PopoverTrigger asChild>

								<Button
									variant="outline"
									className="w-full justify-between capitalize gap-2 h-14"
								>
									<div className={cn("p-2 bg-gray-100 rounded-lg flex", { hidden: !value })}>
										<Landmark />
									</div>
									{value ? (banks.find(bank => bank?.bankName?.toLowerCase() === value)?.bankName) : "Select bank..."}
									<ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-auto" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[375px] p-0 overflow-auto">
								<Command>
									<CommandInput placeholder="Search bank..." />
									<CommandEmpty>
										{isLoading
											? <Loader2 size={20} className="animate-spin opacity-50" />
											: "No bank found."}
									</CommandEmpty>

									<CommandList>
										<CommandGroup>
											{banks.map(bank => (
												<CommandItem
													key={bank.id}
													onSelect={() => {
														field.onChange(bank?.id);
														setValue(bank?.bankName?.toLowerCase());
														setOpen(false);
													}}
													className="capitalize"
												>
													<div className="p-2 bg-gray-100 rounded-lg">
														<Landmark />
													</div>
													{bank.bankName?.toLowerCase()}
													<Check
														className={cn(
															"mr-2 h-4 w-4",
															value === bank.bankName?.toLowerCase() ? "opacity-100" : "opacity-0"
														)}
													/>
												</CommandItem>
											))}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>
					</FormControl>
				</FormItem>
			)}
		/>
	);
}
